import { addYears, format } from "date-fns";
import { useFormik } from "formik";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components/macro";

import type { UsersAPI } from "generated/types";

import { useInviteManagement } from "hooks/state/useInviteManagement";
import { useDomainHandler } from "hooks/useDomainHandler";
import { useGroups } from "hooks/useGroups";
import { useModal } from "hooks/useModal";
import { useProduct } from "hooks/useProduct";
import { useProfile } from "hooks/useProfile";
import { useLanguage } from "hooks/useSelectedLanguage";

import { CombiLicenseProducts, LicenseDurationPerType, ProductType } from "constants/hst-constants";
import { TeacherModals } from "constants/modals-constants";

import { FontAwesomeIcon } from "components/FontAwesomeIcon";
import { InputWithErrorMessage } from "components/InputWithErrorMessage";
import { MultiStepModal } from "components/MultiStepModal";
import { Note } from "components/Note/Note";
import { RadioButton } from "components/RadioButton/RadioButton";
import { Select } from "components/SelectUI/Select";

import type { SelectOptions } from "components/SelectUI/Select.model";
import type { ProductDetails, ProductSkillLevel } from "models/product/ProductDetails";

import type { ElearningThemeInterface } from "themes/elearningInterface.theme";

import { Heading4 } from "styles/elements/Headings";
import { ParagraphLarge, ParagraphMedium } from "styles/elements/Texts";
import { Box, CssFlex } from "styles/helpers/layout";

import { InviteLinkDetails } from "../InviteLinkDetails/InviteLinkDetails";
import { StyledSuccessBanner } from "../InviteLinkDetails/InviteLinkDetails.styled";

import {
  type NewLinkInviteFormFields,
  buildNewLinkInviteFormSchema,
} from "./CreateLinkInviteModal.schema";

export const CreateLinkInviteModal: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const theme = useTheme() as ElearningThemeInterface;
  const { isModalOpen, closeModal } = useModal();
  const { getStudentTermByDomain } = useDomainHandler();
  const { teacherGroups, getTeacherGroups, createGroup } = useGroups();
  const { productsList, fetchProducts, fetchProductDetails, fetchInvitationBundleList } =
    useProduct();
  const { userEducations, fetchUserEducations } = useProfile();
  const { createInvitationLink, setCreatedInvitationLink } = useInviteManagement();

  const isCurrentlyOpen = isModalOpen(TeacherModals.CreateInviteLink);

  const [selectedBusinessProductDetails, setSelectedBusinessProductDetails] =
    useState<ProductDetails | null>(null);

  const [createdLinkDetails, setCreatedLinkDetails] =
    useState<UsersAPI.CreateInvitationLinkResponse | null>(null);

  useEffect(() => {
    if (isCurrentlyOpen) {
      fetchProducts();
      fetchUserEducations();
      getTeacherGroups(true);
    }
  }, [isCurrentlyOpen]);

  const groupSelectOptions = useMemo(() => {
    if (!teacherGroups) {
      return [];
    }

    return teacherGroups.map((group) => ({
      value: group.id,
      label: group.name,
    }));
  }, [teacherGroups]);

  const educationSelectOptions: SelectOptions[] = useMemo(() => {
    if (!userEducations) {
      return [];
    }

    return userEducations
      .filter((education) => !education.archived)
      .map((education) => ({
        label: education.title,
        value: education.id,
      }));
  }, [userEducations]);

  const productSelectOptions: SelectOptions[] = useMemo(() => {
    if (!productsList) {
      return [];
    }

    const isProductExpired = (licenseExpirationDate?: string | Date) => {
      if (!licenseExpirationDate) {
        return false;
      }
      const expirationDate = new Date(licenseExpirationDate);
      return expirationDate < new Date();
    };

    const ddlOptions: SelectOptions[] = productsList.map((prod) => {
      const expired = isProductExpired(prod.licenseExpirationDate);

      return {
        label: prod.title,
        value: `${prod.id}`,
        disabled: expired,
        ...(expired && {
          hoverMessage: t(
            "modal:createLinkInviteModal.steps.productSelection.fields.selectedProducts.tooltip.expiredLicense",
          ),
        }),
      };
    });

    // Only show combi license option if teacher owns an active license for both english and dutch
    const canSendCombiLicense = CombiLicenseProducts.every((productId) => {
      const ownedLicense = productsList.find(({ id }) => id === productId);
      return !!ownedLicense && !isProductExpired(ownedLicense.licenseExpirationDate);
    });

    if (canSendCombiLicense) {
      ddlOptions.push({
        label: t(
          "modal:createLinkInviteModal.steps.productSelection.fields.selectedProducts.options.combiLicense",
        ),
        // Apply ascending order to product ids for combined option
        value: CombiLicenseProducts.sort((a, b) => a - b).join(","),
      });
    }

    return ddlOptions;
  }, [productsList, currentLanguage]);

  const formik = useFormik<NewLinkInviteFormFields>({
    initialValues: {
      selectedEducation: "",
      selectedProducts: [],
      licenseExpirationDate: addYears(new Date(), 1),
      maxUsageLimit: 1,
      groupId: undefined,
      groupAssignmentMethod: "new",
    },
    validationSchema: buildNewLinkInviteFormSchema(),
    validateOnChange: true,
    onSubmit: async (values) => {
      // Resolve groupId: create new group if needed
      let groupId: number | undefined;
      if (values.groupAssignmentMethod === "new") {
        groupId = await createGroup(values.newGroupName);
      } else if (values.groupAssignmentMethod === "existing") {
        groupId = values.groupId && +values.groupId;
      }

      const response = await createInvitationLink({
        licenseExpirationDate: values.licenseExpirationDate.toISOString(),
        productsList: values.selectedProducts.map((p) => +p.id),
        educationId: values.selectedEducation,
        maxUsageLimit: values.maxUsageLimit,
        initialSkillLevel:
          values.selectedProducts.length === 1
            ? values.selectedProducts[0].initialSkillLevel
            : undefined,
        groupId,
      });

      if (!response) {
        // createInvitationLink shows a toast on error; just stop submitting
        formik.setSubmitting(false);
        return;
      }

      setCreatedLinkDetails(response);

      // Force refresh of invite list
      fetchInvitationBundleList();

      // Reset form fields after submit is complete
      formik.setSubmitting(false);
    },
  });

  // Pre-select option to assign users to existing group if there are available groups,
  // otherwise fall back to creating a new group
  useEffect(() => {
    if (groupSelectOptions.length > 0) {
      formik.setFieldValue("groupAssignmentMethod", "existing");
    } else {
      formik.setFieldValue("groupAssignmentMethod", "new");
    }
  }, [formik.setFieldValue, groupSelectOptions]);

  useEffect(() => {
    const isBusinessLicense = formik.values.selectedProducts.some(
      ({ type }) => type === ProductType.BUSINESS,
    );

    // Fetch product details for business product so user can choose initial goal level
    if (formik.values.selectedProducts.length === 1 && isBusinessLicense) {
      fetchProductDetails(formik.values.selectedProducts[0].id, {
        skipSaveResultInStore: true,
      }).then((businessProductDetails) =>
        setSelectedBusinessProductDetails(businessProductDetails),
      );
    }

    // Expiration date will vary based on whether selected product is business or not
    const selectedLicenseType = isBusinessLicense ? ProductType.BUSINESS : ProductType.REGULAR;
    formik.setFieldValue(
      "licenseExpirationDate",
      addYears(new Date(), LicenseDurationPerType[selectedLicenseType]),
    );
  }, [formik.setFieldValue, formik.values.selectedProducts]);

  const availableSkillLevels: ProductSkillLevel[] = useMemo(() => {
    if (
      selectedBusinessProductDetails &&
      formik.values.selectedProducts.length === 1 &&
      formik.values.selectedProducts[0].type === ProductType.BUSINESS &&
      formik.values.selectedProducts[0].id === selectedBusinessProductDetails.id
    ) {
      return (
        selectedBusinessProductDetails.levels.filter((level) => level.selectable) ||
        ([] as ProductSkillLevel[])
      );
    }
    return [];
  }, [formik.values.selectedProducts, selectedBusinessProductDetails]);

  const createLinkInviteSteps = [
    {
      id: "select-product-step",
      label: t("modal:createLinkInviteModal.steps.productSelection.title"),
      iconName: "fas fa-books",
      content: (
        <CssFlex flexDirection="column" gap={1}>
          <CssFlex flexDirection="column" gap={0.5}>
            <Heading4>{t("modal:createLinkInviteModal.steps.productSelection.title")}</Heading4>
            <Note variant="warning" dataCy="link-invite-security-warning">
              {t("modal:createLinkInviteModal.securityWarning")}
            </Note>
            <CssFlex flexDirection="column" gap={1.5} paddingTop="1rem" paddingBottom="1rem">
              <Box>
                <label
                  id="create-link-invite-product-selector-label"
                  htmlFor="create-link-invite-product-selector"
                >
                  {t(
                    "modal:createLinkInviteModal.steps.productSelection.fields.selectedProducts.label",
                  )}
                </label>
                <Select
                  showSearchBox
                  aria-labelledby="create-link-invite-product-selector-label"
                  dataCy="product-selector"
                  id="create-link-invite-product-selector"
                  options={productSelectOptions}
                  name="selectedProducts"
                  placeholder={t(
                    "modal:createLinkInviteModal.steps.productSelection.fields.selectedProducts.placeholder",
                  )}
                  showPlaceholderInOptionList={false}
                  value={formik.values.selectedProducts.map(({ id }) => id).join(",")}
                  onChange={(e) => {
                    // To prevent issues with combi option if products are returned in different order,
                    // we must make sure to sort ids in ascending order when setting selected value
                    formik.setFieldValue(
                      "selectedProducts",
                      productsList
                        ?.filter(({ id: productId }) =>
                          e.target.value.split(",").includes(`${productId}`),
                        )
                        .map((product) => ({
                          id: product.id,
                          name: product.title,
                          type: product.type,
                        }))
                        .sort((a, b) => a.id - b.id) || [],
                    );
                  }}
                />
              </Box>
              {/* -- License expiration date as info (auto-computed from product) -- */}
              {formik.values.selectedProducts.length > 0 && (
                <Box>
                  <ParagraphMedium>
                    {t(
                      "modal:createLinkInviteModal.steps.productSelection.fields.licenseExpirationDate.label",
                    )}{" "}
                    <strong data-cy="license-expiration-date-info">
                      {format(formik.values.licenseExpirationDate, "dd/MM/yyyy")}
                    </strong>
                  </ParagraphMedium>
                </Box>
              )}
              <Box>
                <label
                  id="create-link-invite-education-selector-label"
                  htmlFor="create-link-invite-education-selector"
                >
                  {t(
                    "modal:createLinkInviteModal.steps.productSelection.fields.selectedEducation.label",
                  )}
                </label>
                <Select
                  showSearchBox
                  aria-labelledby="create-link-invite-education-selector-label"
                  dataCy="education-selector"
                  id="create-link-invite-education-selector"
                  options={educationSelectOptions}
                  name="selectedEducation"
                  placeholder={t(
                    "modal:createLinkInviteModal.steps.productSelection.fields.selectedEducation.placeholder",
                  )}
                  showPlaceholderInOptionList={false}
                  value={formik.values.selectedEducation}
                  onChange={formik.handleChange}
                />
              </Box>
              {/* -- Max usage limit -- */}
              <Box>
                <label htmlFor="create-link-invite-max-usage-limit-input">
                  {t(
                    "modal:createLinkInviteModal.steps.productSelection.fields.maxUsageLimit.label",
                  )}
                </label>
                <InputWithErrorMessage
                  dataCy="max-usage-limit-input"
                  errorMessage={formik.errors.maxUsageLimit}
                  hasError={!!(formik.errors.maxUsageLimit && formik.touched.maxUsageLimit)}
                  id="create-link-invite-max-usage-limit-input"
                  name="maxUsageLimit"
                  type="number"
                  min={1}
                  value={formik.values.maxUsageLimit}
                  onChange={formik.handleChange}
                />
                <ParagraphMedium>
                  {t(
                    "modal:createLinkInviteModal.steps.productSelection.fields.maxUsageLimit.hint",
                    {
                      studentTerm: getStudentTermByDomain({ usePlural: true }),
                    },
                  )}
                </ParagraphMedium>
              </Box>
            </CssFlex>
            {/* -- Initial skill level (available only when one business prod is selected) -- */}
            {formik.values.selectedProducts.length === 1 &&
              formik.values.selectedProducts[0].type === ProductType.BUSINESS && (
                <>
                  <label htmlFor="create-link-invite-initial-skill-level-input">
                    {t(
                      "modal:createLinkInviteModal.steps.productSelection.fields.initialSkillLevel.label",
                    )}
                  </label>
                  <ParagraphMedium>
                    {t(
                      "modal:createLinkInviteModal.steps.productSelection.fields.initialSkillLevel.description",
                    )}
                  </ParagraphMedium>
                  {availableSkillLevels.map((productSkillLevel) => (
                    <RadioButton
                      bulletColor="primary"
                      bulletStyle="thin"
                      key={productSkillLevel.id}
                      checked={
                        productSkillLevel.level ===
                        formik.values.selectedProducts[0].initialSkillLevel
                      }
                      dataCy="initial-skill-level-radio"
                      description={productSkillLevel.description || ""}
                      label={productSkillLevel.label}
                      name="initialSkillLevel"
                      value={productSkillLevel.level}
                      onChange={(e) => {
                        formik.setFieldValue(
                          "selectedProducts",
                          formik.values.selectedProducts.map((selectedProduct) => ({
                            ...selectedProduct,
                            initialSkillLevel: +e.target.value,
                          })),
                        );
                      }}
                    />
                  ))}
                </>
              )}
          </CssFlex>
        </CssFlex>
      ),
      disableNextButton:
        !formik.dirty ||
        !!formik.errors.selectedEducation ||
        !!formik.errors.selectedProducts ||
        !!formik.errors.maxUsageLimit,
    },
    {
      id: "assign-group-step",
      label: t("modal:createLinkInviteModal.steps.assignGroup.title"),
      iconName: "fas fa-users",
      content: (
        <form noValidate onSubmit={formik.handleSubmit}>
          <CssFlex flexDirection="column" gap={0.5}>
            <Heading4>{t("modal:createLinkInviteModal.steps.assignGroup.title")}</Heading4>
            <ParagraphLarge>
              {t("modal:createLinkInviteModal.steps.assignGroup.description", {
                studentTerm: getStudentTermByDomain({ usePlural: true }),
              })}
            </ParagraphLarge>
            <CssFlex flexDirection="column" gap={1.5} paddingTop="1.5rem">
              <RadioButton
                bulletColor="primary"
                bulletStyle="thin"
                checked={formik.values.groupAssignmentMethod === "existing"}
                disabled={groupSelectOptions.length === 0}
                dataCy="assign-to-existing-group-radio"
                label={t(
                  "modal:createLinkInviteModal.steps.assignGroup.fields.groupAssignmentMethod.options.existing",
                )}
                name="groupAssignmentMethod"
                value="existing"
                onChange={formik.handleChange}
              />
              {formik.values.groupAssignmentMethod === "existing" && (
                <Box paddingLeft="4.5rem" paddingBottom="1rem">
                  <label
                    id="create-link-invite-group-selector-label"
                    htmlFor="create-link-invite-group-selector"
                  >
                    {t("modal:createLinkInviteModal.steps.assignGroup.fields.groupId.label")}
                  </label>
                  <Select
                    showSearchBox
                    id="create-link-invite-group-selector"
                    aria-labelledby="create-link-invite-group-selector-label"
                    dataCy="group-selector"
                    options={groupSelectOptions}
                    name="groupId"
                    showPlaceholderInOptionList={false}
                    value={formik.values.groupId}
                    onChange={formik.handleChange}
                  />
                </Box>
              )}
              <RadioButton
                bulletColor="primary"
                bulletStyle="thin"
                checked={formik.values.groupAssignmentMethod === "new"}
                dataCy="assign-to-new-group-radio"
                label={t(
                  "modal:createLinkInviteModal.steps.assignGroup.fields.groupAssignmentMethod.options.new",
                )}
                name="groupAssignmentMethod"
                value="new"
                onChange={formik.handleChange}
              />
              {formik.values.groupAssignmentMethod === "new" && (
                <Box paddingLeft="4.5rem" paddingBottom="1rem">
                  <label htmlFor="create-link-invite-group-name-input">
                    {t("modal:createLinkInviteModal.steps.assignGroup.fields.newGroupName.label")}*
                  </label>
                  <InputWithErrorMessage
                    dataCy="group-name-input"
                    errorMessage={formik.errors.newGroupName}
                    hasError={!!(formik.errors.newGroupName && formik.touched.newGroupName)}
                    id="create-link-invite-group-name-input"
                    name="newGroupName"
                    value={formik.values.newGroupName}
                    onChange={formik.handleChange}
                  />
                </Box>
              )}
              <RadioButton
                bulletColor="primary"
                bulletStyle="thin"
                checked={formik.values.groupAssignmentMethod === "none"}
                dataCy="assign-to-no-group-radio"
                label={t(
                  "modal:createLinkInviteModal.steps.assignGroup.fields.groupAssignmentMethod.options.none",
                )}
                name="groupAssignmentMethod"
                value="none"
                onChange={formik.handleChange}
              />
              {formik.values.groupAssignmentMethod === "new" && (
                <Note variant="info">
                  {t("modal:createLinkInviteModal.steps.assignGroup.addToNewGroupNote", {
                    studentTerm: getStudentTermByDomain({ usePlural: true }),
                  })}
                </Note>
              )}
              {formik.values.groupAssignmentMethod === "existing" && (
                <Note variant="info">
                  {t("modal:createLinkInviteModal.steps.assignGroup.addToExistingGroupNote", {
                    studentTerm: getStudentTermByDomain({ usePlural: true }),
                  })}
                </Note>
              )}
              {formik.isValid && (
                <Note variant="warning">
                  {t("modal:createLinkInviteModal.steps.assignGroup.invoiceWarning")}
                </Note>
              )}
            </CssFlex>
          </CssFlex>
        </form>
      ),
      nextButtonLabel: t("modal:createLinkInviteModal.steps.assignGroup.nextButton"),
      disableNextButton: !formik.dirty || !formik.isValid || formik.isSubmitting,
      onNextButtonClick: () => {
        formik.setSubmitting(true);
        formik.submitForm();
      },
    },
    {
      id: "link-details-step",
      label: t("modal:createLinkInviteModal.steps.linkDetails.title"),
      iconName: "fas fa-link",
      content: createdLinkDetails ? (
        <CssFlex flexDirection="column" gap={1}>
          <StyledSuccessBanner
            flexDirection="column"
            alignItems="center"
            gap={0.5}
            data-cy="link-invite-success-banner"
          >
            <FontAwesomeIcon
              iconName="fas fa-check-circle"
              fontSize="2rem"
              iconColor={theme.palette.feedback.success.brand}
            />
            <ParagraphLarge fontWeight="bold">
              {t("modal:createLinkInviteModal.steps.linkDetails.successBanner.title")}
            </ParagraphLarge>
            <ParagraphMedium>
              {t("modal:createLinkInviteModal.steps.linkDetails.successBanner.description", {
                studentTerm: getStudentTermByDomain({ usePlural: true }),
              })}
            </ParagraphMedium>
          </StyledSuccessBanner>
          <InviteLinkDetails
            invitationId={createdLinkDetails.id}
            usage={{
              current: createdLinkDetails.currentUsage,
              max: createdLinkDetails.maxUsageLimit,
            }}
            products={createdLinkDetails.productNames}
            educationName={createdLinkDetails.educationName}
            groupName={createdLinkDetails.groupName ?? null}
            licenseExpirationDate={new Date(createdLinkDetails.licenseExpirationDate)}
          />
        </CssFlex>
      ) : null,
      nextButtonLabel: t("modal:createLinkInviteModal.steps.linkDetails.doneButton"),
      hidePrevButton: true,
      hideCancelButton: true,
      disableNextButton: !createdLinkDetails,
      onNextButtonClick: () => {
        formik.resetForm();
        setCreatedLinkDetails(null);
        setCreatedInvitationLink(null);
        closeModal();
      },
    },
  ];

  return (
    <MultiStepModal
      id={TeacherModals.CreateInviteLink}
      title={t("modal:createLinkInviteModal.title")}
      steps={createLinkInviteSteps}
      onClose={() => {
        formik.resetForm();
        setCreatedLinkDetails(null);
        setCreatedInvitationLink(null);
      }}
      autoCloseOnLastNextButtonClick={false}
    />
  );
};
